/**
 * MoveNet wrapper for the pose classifier project kind.
 *
 * MoveNet finds 17 body keypoints (nose, eyes, ears, shoulders, elbows,
 * wrists, hips, knees, ankles) per person. We feed those into a small
 * MLP and let the kid train it like any other classifier — the same
 * "show the model what you mean" workflow as image / audio, just with
 * a different input shape.
 *
 * Lazy-loaded so the pose-detection bundle only ships when a kid opens
 * a pose project. Kept inside the in-browser TF.js stack we already
 * carry — runtime is `tfjs`, not MediaPipe wasm.
 */

export const POSE_KEYPOINTS = 17;
/** Each keypoint contributes (x, y, score). */
export const POSE_FEATURES_PER_KEYPOINT = 3;
export const POSE_VECTOR_LENGTH = POSE_KEYPOINTS * POSE_FEATURES_PER_KEYPOINT;

/**
 * Skeleton edges between MoveNet keypoint indices, drawn as line
 * segments to give the kid a Teachable-Machine-style live overlay.
 * Indices match MoveNet's standard 17-keypoint COCO ordering:
 *   0 nose, 1/2 eyes, 3/4 ears, 5/6 shoulders, 7/8 elbows,
 *   9/10 wrists, 11/12 hips, 13/14 knees, 15/16 ankles.
 * Face edges (eyes / ears) are intentionally omitted — they make
 * the overlay noisy and aren't load-bearing for the classifier.
 */
export const POSE_SKELETON_EDGES: ReadonlyArray<readonly [number, number]> = [
  [5, 6], // shoulders
  [5, 7], // L shoulder → L elbow
  [7, 9], // L elbow → L wrist
  [6, 8], // R shoulder → R elbow
  [8, 10], // R elbow → R wrist
  [5, 11], // L shoulder → L hip
  [6, 12], // R shoulder → R hip
  [11, 12], // hips
  [11, 13], // L hip → L knee
  [13, 15], // L knee → L ankle
  [12, 14], // R hip → R knee
  [14, 16], // R knee → R ankle
];

type RawKeypoint = { x: number; y: number; score?: number };
type PoseDetector = {
  estimatePoses: (
    input: ImageBitmap | HTMLImageElement | HTMLCanvasElement | HTMLVideoElement,
  ) => Promise<{ keypoints: RawKeypoint[] }[]>;
};

let detectorPromise: Promise<PoseDetector> | null = null;

/**
 * Self-hosted MoveNet SinglePose Lightning v4 weights.
 *
 * The pose-detection package's default URL points at `tfhub.dev/...`
 * which Google retired in late 2024 — fetches return 404 and the
 * live skeleton overlay never paints. Kaggle's replacement requires
 * authenticated downloads.
 *
 * We bundle the weights ourselves under `public/models/movenet/` so
 * pose works offline, on locked-down school networks, and survives
 * any future upstream URL churn. The files come from
 * https://github.com/vladmandic/movenet (a re-pack of the original
 * tfhub v4 graph; same architecture + weights, single binary file
 * instead of two shards).
 *
 * To re-fetch (e.g. on rebuild):
 *   curl -L -o public/models/movenet/model.json \\
 *     https://raw.githubusercontent.com/vladmandic/movenet/main/models/movenet-lightning.json
 *   curl -L -o public/models/movenet/movenet-lightning.bin \\
 *     https://raw.githubusercontent.com/vladmandic/movenet/main/models/movenet-lightning.bin
 */
const MOVENET_MODEL_URL = '/models/movenet/model.json';

// Once the detector boot fails (missing dependency, dead model URL,
// blocked network) we cache the rejection so subsequent calls fail
// fast. Without this cache the overlay loop hammered the failing
// model.json fetch every 100 ms — 600 doomed requests per minute.
// The kid can recover by reloading the page (which gives us a new
// module instance and a fresh `detectorPromise`).
let detectorFailed: Error | null = null;

async function getDetector(): Promise<PoseDetector> {
  if (detectorFailed) throw detectorFailed;
  if (!detectorPromise) {
    detectorPromise = (async () => {
      // Initialise the TF.js backend (webgl) before pose-detection
      // tries to use it. Without this the first detection throws
      // `No backend found in registry` because pose-detection imports
      // tfjs-core but not a backend package directly.
      const { getTf } = await import('./tfBackend');
      await getTf();
      const pd = await import('@tensorflow-models/pose-detection');
      const model = pd.SupportedModels.MoveNet;
      // SinglePose Lightning is small and fast — fine for a kid-scale
      // dataset on a $200 Chromebook.
      return (await pd.createDetector(model, {
        modelType: pd.movenet.modelType.SINGLEPOSE_LIGHTNING,
        modelUrl: MOVENET_MODEL_URL,
      })) as unknown as PoseDetector;
    })().catch((err) => {
      detectorPromise = null;
      detectorFailed = err instanceof Error ? err : new Error(String(err));
      throw detectorFailed;
    });
  }
  return detectorPromise;
}

/**
 * Convert a raw MoveNet `keypoints` array into the flat float vector
 * the classifier MLP consumes. x and y are normalised to [0, 1] using
 * the source dimensions so the model is independent of the kid's
 * camera resolution. Pure helper, separately tested.
 */
export function keypointsToVector(
  keypoints: ReadonlyArray<RawKeypoint>,
  width: number,
  height: number,
): Float32Array {
  const out = new Float32Array(POSE_VECTOR_LENGTH);
  const w = width > 0 ? width : 1;
  const h = height > 0 ? height : 1;
  for (let i = 0; i < POSE_KEYPOINTS; i++) {
    const kp = keypoints[i];
    const base = i * POSE_FEATURES_PER_KEYPOINT;
    if (!kp) {
      // Missing keypoint: leave zeros. (MoveNet reliably returns 17 but
      // be defensive — the MLP treats zero-score as "ignore" anyway.)
      continue;
    }
    out[base] = clamp01(kp.x / w);
    out[base + 1] = clamp01(kp.y / h);
    out[base + 2] = clamp01(kp.score ?? 0);
  }
  return out;
}

function clamp01(n: number): number {
  if (Number.isNaN(n)) return 0;
  if (n < 0) return 0;
  if (n > 1) return 1;
  return n;
}

/**
 * Run MoveNet on a single sample blob and return the flat pose vector.
 * The vector layout is: 17 keypoints × (x, y, score) = 51 floats.
 */
export async function detectPoseVector(blob: Blob): Promise<Float32Array> {
  const bitmap = await createImageBitmap(blob);
  try {
    const detector = await getDetector();
    const poses = await detector.estimatePoses(bitmap);
    const first = poses[0];
    if (!first) {
      // No person detected. Return the all-zero vector — the classifier
      // will treat it as "no pose" and learn that pattern itself.
      return new Float32Array(POSE_VECTOR_LENGTH);
    }
    return keypointsToVector(first.keypoints, bitmap.width, bitmap.height);
  } finally {
    bitmap.close();
  }
}

/**
 * Live variant for the predict panel: takes an HTMLVideoElement and
 * returns the flat pose vector. The panel calls this each frame.
 */
export async function detectPoseVectorFromVideo(video: HTMLVideoElement): Promise<Float32Array> {
  const detector = await getDetector();
  const poses = await detector.estimatePoses(video);
  const first = poses[0];
  if (!first) return new Float32Array(POSE_VECTOR_LENGTH);
  const w = video.videoWidth || video.clientWidth || 1;
  const h = video.videoHeight || video.clientHeight || 1;
  return keypointsToVector(first.keypoints, w, h);
}

/** A keypoint with x/y in *normalised* video coords (0..1) and a
 *  confidence score. The capture overlay wants this shape so it can
 *  scale to whatever size the canvas is being rendered at — using
 *  raw pixel coords would couple the overlay to the source video
 *  resolution. */
export interface NormalizedKeypoint {
  readonly x: number;
  readonly y: number;
  readonly score: number;
}

/**
 * Live variant for the *capture* overlay (and the predict overlay).
 * Returns the 17 raw keypoints in normalised coordinates so the
 * caller can draw dots + skeleton edges at any canvas size. Returns
 * `null` when no person is in frame so the overlay can clear cleanly
 * instead of drawing a frozen ghost pose.
 */
export async function detectKeypointsFromVideo(
  video: HTMLVideoElement,
): Promise<readonly NormalizedKeypoint[] | null> {
  const detector = await getDetector();
  const poses = await detector.estimatePoses(video);
  const first = poses[0];
  if (!first) return null;
  const w = video.videoWidth || video.clientWidth || 1;
  const h = video.videoHeight || video.clientHeight || 1;
  const out: NormalizedKeypoint[] = [];
  for (let i = 0; i < POSE_KEYPOINTS; i++) {
    const kp = first.keypoints[i];
    if (!kp) {
      out.push({ x: 0, y: 0, score: 0 });
      continue;
    }
    out.push({
      x: clamp01(kp.x / w),
      y: clamp01(kp.y / h),
      score: clamp01(kp.score ?? 0),
    });
  }
  return out;
}

/**
 * Trigger the dynamic import + detector boot eagerly without running
 * inference. Pose project views call this on mount so by the time the
 * kid opens a class's webcam, MoveNet is already warm and the live
 * skeleton overlay paints on the first overlay tick. Safe to call
 * repeatedly — `getDetector` memoises the promise.
 */
export function warmUpDetector(): void {
  void getDetector().catch(() => {
    // Network / older-browser failures are surfaced when the overlay
    // loop tries to detect; pre-warming has nothing useful to do
    // here.
  });
}

/** Test-only escape hatch for resetting the lazily-loaded detector. */
export const __test = {
  resetDetector(): void {
    detectorPromise = null;
  },
};
